
import moment from 'moment'
import Api from '@/Plugins/api'
import Order from '@/Models/Order'
import refunder from './refunder'

const PER_PAGE = 15;
const DATE_FORMAT = 'YYYY-MM-DD';

const state = () => ({
  /**
   * @type Boolean
   */
  show: false,

  /**
   * @type Boolean
   */
  loading: false,

  /**
   * @type moment
   */
  date: undefined,

  /**
   * @type Number
   */
  id: null,

  /**
   * @type Array
   */
  orders: [],

  /**
   * @type Number
   */
  offset: 0,

  /**
   * @type Boolean
   */
  loadMoreOrders: true,

  /**
   * @type String
   */
  datePickerMin: '',

  /**
   * @type String
   */
  datePickerMax: '',

  /**
   * @type Object
   */
  filters: {},
})

const getters = {
  orders: (state, getters) => state.orders
    .filter(order => {
      if (! order.closed) {
        return false
      }
      if (getters._filterValue('user', order.tenantUser_id)) {
        return false
      }
      if (getters._filterValue('table', order.table_id)) {
        return false
      }
      if (getters._filterRange('guests', order.guests)) {
        return false
      }
      if (getters._filterRange('amount', order.total)) {
        return false
      }
      if (state.filters.opened?.time) {
        const [openedHour, openedMinute] = state.filters.opened.time.split(":")
        const openedMoment = order.openedMoment
          .hour(openedHour)
          .minute(openedMinute)
          .seconds(0)
        if (order.openedMoment.diff(openedMoment) < 0) {
          return false
        }
      }
      if (state.filters.closed?.time) {
        const [closedHour, closedMinute] = state.filters.closed.time.split(":")
        const closedMoment = order.closedMoment
          .hour(closedHour)
          .minute(closedMinute)
          .seconds(0)
        if (order.closedMoment.diff(closedMoment) > 0) {
          return false
        }
      }
      return true
    }),
  _filterValue: state => (prop, value) => state.filters[prop]
    && state.filters[prop].value !== null
    && state.filters[prop].value != value,
  _filterRange: state => (prop, value) => {
    const min = state.filters[prop]?.min
    if (min !== undefined && Number(value).lessThan(min)) {
      return true
    }
    const max = state.filters[prop]?.max
    if (max !== undefined && Number(value).greaterThan(max)) {
      return true
    }
    return false
  },

  filterItems: (state, getters) => filter => getters[`_${filter}Filter`],
  _userFilter: (state, getters, rootState, rootGetters) => {
    const users = rootGetters['rummage/waiters']
      .filter(user => user.id != rootState.user.id && !! user.pin)
      .map(user => ({
        name: user.surname ? `${user.name} ${user.surname}` : user.name,
        value: user.id,
      }))
      .sortByString('name')

    users.unshift({
      name: rootGetters['user/fullName'],
      value: rootState.user.id,
    })

    return users
  },
  _tableFilter: (state, getters, rootState, rootGetters) => rootGetters['rummage/rooms']
    .flatMap(room => [
      { subheader: room.name },
      ...rootGetters['rummage/tables'](room.id)
        .map(table => ({
          name: table.name,
          value: table.id,
        }))
    ]),
  _guestsFilter: () => {
    const guests = Array(20).fill()
      .map((_, i) => ({
        value: i + 1,
      }))
    guests.push({
      name: '> 20',
      value: 9999,
    })

    return guests
  },
}

const mutations = {
  show (state) {
    state.show = true
  },
  hide (state) {
    state.show = false
  },
  loading (state, v = true) {
    state.loading = Boolean(v)
  },
  date (state, v) {
    state.id = null
    state.date = v
  },
  id (state, v) {
    state.id = v
    state.date = undefined
  },
  reset (state) {
    state.orders = []
    state.offset = 0
    state.loadMoreOrders = true
    state.filters = {}
  },
  merge (state, orders) {
    state.offset += orders.length
    if (orders.length < PER_PAGE) {
      state.loadMoreOrders = false
    }
    state.orders = state.orders.concat(orders.map(o => new Order(o)))
  },
  datePickerRange (state, {min, max}) {
    state.datePickerMin = min.format(DATE_FORMAT)
    state.datePickerMax = max.format(DATE_FORMAT)
  },
  filter (state, v) {
    state.filters = {
      ...state.filters,
      [v.key]: v,
    }
  },
}

const actions = {
  open ({ commit, dispatch }) {
    commit('reset')
    commit('loading')
    commit('date', moment().utc())
    commit('datePickerRange', {
      min: moment().utc().subtract(1, 'years'),
      max: moment().utc(),
    })
    commit('show')
    setTimeout(() => dispatch('fetch'), 150)
  },
  close ({ commit }) {
    commit('hide')
  },
  changeDate ({ commit, dispatch }, date) {
    commit('reset')
    commit('loading')
    commit('date', moment.utc(date, DATE_FORMAT, true))
    dispatch('fetch')
  },
  fetch ({ state, commit, dispatch }) {
    commit('loading')
    Api('getOrdersForDate2', {
      date: state.date.format(DATE_FORMAT),
      offset: state.offset,
      limit: PER_PAGE,
    })
      .then(r => commit('merge', r.data))
      .catch(e => dispatch('xError', e, { root: true }))
      .finally(() => commit('loading', false))
  },
  searchOrder ({ state, commit, rootGetters }, id) {
    commit('reset')
    commit('loading')
    commit('id', parseInt(id))
    Api('getOrder', { id: state.id })
      .then(r => {
        const opened = moment.utc(r.data?.order?.opened, 'YYYY-MM-DD HH:mm:ss', true)
        if (! opened.isValid()) {
          return
        }
        const today = moment().utc().startOf('day').diff(opened.startOf('day')) === 0
        if (today || rootGetters['user/can']('editOlderOrders')) {
          commit('merge', [r.data.order])
        }
      })
      .catch(e => dispatch('xError', e, { root: true }))
      .finally(() => commit('loading', false))
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
  modules: {
    refunder,
  },
}
